import type { RealtimeClient } from '@openai/realtime-api-beta';

type SetFn<T = any> = (value: T | ((prev: T) => T)) => void;

interface RegisterGetInvestmentDeps {
  setInvestmentFilter: SetFn<any>;
  setTopProperties: SetFn<any>;
  setView: (v: string) => void;
  setTopInvestments: SetFn<any>;
  filterInvestments: (data: any) => Promise<any>;
}

export function registerGetInvestmentTool(
  client: RealtimeClient,
  {
    setInvestmentFilter,
    setTopProperties,
    setView,
    setTopInvestments,
    filterInvestments,
  }: RegisterGetInvestmentDeps
) {
  client.addTool(
    {
      name: 'get_investment',
      description:
        'Search real-estate investment products/plans. Parameters in English; replies in Urdu.',
      parameters: {
        type: 'object',
        properties: {
          intent: {
            type: 'string',
            description: 'User intent inferred from NLU.',
            enum: ['invest', 'buy_home', 'rent_to_own', 'seek_discount'],
          },
          productKind: {
            type: 'string',
            description: 'Product type family.',
            enum: ['investment', 'home_purchase', 'rent_to_own', 'discount_offer'],
          },
          category: {
            type: 'string',
            description: 'Residential or Commercial focus.',
            enum: ['residential', 'commercial'],
          },
          projectIds: {
            type: 'array',
            description: 'Specific project IDs to prefer or filter.',
            items: { type: 'string' },
          },
          planId: {
            type: 'string',
            description: 'If the user referenced a specific plan/product by id or slug.',
          },
          budgetMonthlyMin: { type: 'number', description: 'Min acceptable monthly installment (PKR).' },
          budgetMonthlyMax: { type: 'number', description: 'Max acceptable monthly installment (PKR).' },
          downPaymentPercentMin: { type: 'number', description: 'Min acceptable down payment %.' },
          downPaymentPercentMax: { type: 'number', description: 'Max acceptable down payment %.' },
          tenureMonthsMin: {
            type: 'integer',
            description: 'Min tenure in months. If user states years, convert to months before calling.',
          },
          tenureMonthsMax: {
            type: 'integer',
            description: 'Max tenure in months. If user states years, convert to months before calling.',
          },
          expectedYieldMin: { type: 'number', description: 'Min target total gain % (if applicable).' },
          expectedYieldMax: { type: 'number', description: 'Max target total gain % (if applicable).' },
          preferProducts: {
            type: 'array',
            description: 'Product IDs suggested by routing rules.',
            items: { type: 'string' },
          },
        },
        // required: ['intent'],
      },
    },
    async (key: any) => {
      const data = { ...key, page: 1, pageSize: 50 };
      setInvestmentFilter(data);

      const invRes = await filterInvestments(data);
      const { plans, count } = invRes;

      setTopProperties({ properties: [], count: 0, currentCount: 0 });
      setView('products');
      setTopInvestments({ plans, count, currentCount: plans?.length });

      return plans?.length
        ? `Investment plans available: ${plans.length}`
        : 'No investment plan available!';
    }
  );
}
